import React from 'react'
import { Button } from '@surecompanies/core_components'
import { Popover, Tooltip } from 'antd'
import { FilterFilled } from '@ant-design/icons'
import isNil from 'lodash/isNil'
import { UserListRow } from '../../api/dto/user-invitation.dto'
import { NoneText, sureco } from '../shared/finance.styles'
import {
  AccessValue,
  CellText,
  ClientPopoverItem,
  ClientPopoverList,
  CompanyCell,
  CompanyName,
  EmailCell,
  NoCompaniesText,
  StatusPill,
} from './UserManagement.styles'
import {
  formatCellDate,
  formatDate,
  formatTime,
  getDisplayStatusConfig,
  type RoleAccessConfig,
} from './userDisplay'

/** How each cell of the roster renders. Held apart from the column list so the
 * columns read as a table of contents. */

export const renderEmailCell = (email: string) => (
  <EmailCell title={email}>{email}</EmailCell>
)

export const renderNameCell = (fullName: string | null) =>
  isNil(fullName) ? (
    <NoneText>—</NoneText>
  ) : (
    <CellText title={fullName}>{fullName}</CellText>
  )

// Dates stay on one line to hold the row pitch; the time of day is exact detail
// that few rows are read for, so it moves to the hover title.
export const renderDateTime = (iso: string | null) =>
  iso ? (
    <CellText title={`${formatDate(iso)} ${formatTime(iso)}`}>
      {formatCellDate(iso)}
    </CellText>
  ) : (
    <NoneText>—</NoneText>
  )

/** A header short enough to hold one line, with the full wording on hover. */
export const abbreviatedTitle = (short: string, full: string) => (
  <Tooltip title={full}>
    <span>{short}</span>
  </Tooltip>
)

/** Teal once the column is filtering, so a narrowed list says so in its header. */
export const renderFilterIcon = (isFiltering: boolean) => (
  <FilterFilled style={{ color: isFiltering ? sureco.teal : sureco.fg3 }} />
)

export const renderAccessCell = ({
  label,
  tone,
  tooltip,
}: RoleAccessConfig) => {
  // No role reads as the dash every other empty cell in this table uses, rather
  // than a column of "No" competing with the states that mean something.
  if (tone === 'none') {
    return (
      <Tooltip title={tooltip ?? 'Does not hold this role'}>
        <NoneText>—</NoneText>
      </Tooltip>
    )
  }
  const value = <AccessValue $tone={tone}>{label}</AccessValue>
  return isNil(tooltip) ? value : <Tooltip title={tooltip}>{value}</Tooltip>
}

export const renderCompanyCell = (record: UserListRow) => {
  const keys = record.billingClientPublicKeys
  if (keys.length === 0) return <NoCompaniesText>No companies</NoCompaniesText>
  // Names are resolved server-side and arrive with the row, so they render
  // immediately with no separate lookup or UUID flash.
  const names = record.billingClientNames
  const [firstName, ...restNames] = names

  if (restNames.length === 0) {
    return (
      <CompanyCell>
        <CompanyName title={firstName}>{firstName}</CompanyName>
      </CompanyCell>
    )
  }

  return (
    <CompanyCell>
      <CompanyName title={firstName}>{firstName}</CompanyName>
      <Popover
        content={
          <ClientPopoverList>
            {names.map((name, index) => (
              <ClientPopoverItem key={keys[index]}>{name}</ClientPopoverItem>
            ))}
          </ClientPopoverList>
        }
        title="Billing profiles"
        trigger="click"
      >
        <Button type="link" size="small">
          +{restNames.length} more
        </Button>
      </Popover>
    </CompanyCell>
  )
}

export const renderStatusCell = (record: UserListRow) => {
  const config = getDisplayStatusConfig(record)
  const pill = <StatusPill $variant={config.variant}>{config.label}</StatusPill>
  return isNil(config.tooltip) ? (
    pill
  ) : (
    <Tooltip title={config.tooltip}>{pill}</Tooltip>
  )
}
